import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Divider,
  Checkbox,
  ListItemText
} from '@mui/material';
import { PrimaryButton, SecondaryButton, FormInput } from './ui';
import { Add as AddIcon } from '@mui/icons-material';
import { getKitchens } from '../api/api';
import type { Kitchen, SpiceLevel } from '../types/menu';
import { SPICE_LEVELS } from '../types/menu';
import { ALLERGEN_LABELS, ALLERGEN_FREE_KEY, ALLERGEN_FREE_LABEL } from '../config/allergens';
import { DietaryDot, DIETARY_LABELS } from './DietaryDot';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import MenuItemImageUpload from './MenuItemImageUpload';
import { useImageUpload } from '../hooks/useImageUpload';
import { IMAGE_GATEWAY_URL } from '../config/images';

interface ItemFormData {
  name: string;
  description?: string;
  price: number;
  available: boolean;
  quantity?: number | null;
  freeToppingsCount?: number;
  extraToppingPrice?: number;
  kitchenId?: number;
  spiceLevel?: SpiceLevel;
  allowsSpiceCustomization?: boolean;
  imageKey?: string;
  allergens?: string[];
  allergenNote?: string;
  /** Positive "no known allergens" declaration; mutually exclusive with allergens */
  allergenFree?: boolean;
  /** Dietary marker (VEG/NON_VEG/VEGAN); 'NONE' clears back to undeclared */
  dietaryType?: string;
}

interface EditItemFormProps {
  initialData: {
    id?: number;
    name: string;
    description: string;
    price: string;
    available: boolean;
    quantity: string;
    freeToppingsCount?: string;
    extraToppingPrice?: string;
    kitchenId?: string;
    spiceLevel?: string;
    allowsSpiceCustomization?: boolean;
    imageKey?: string;
    imageUrl?: string;
    allergens?: string[];
    allergenNote?: string;
    allergenFree?: boolean;
    dietaryType?: string;
  };
  vendorId?: number;
  onSubmit: (data: ItemFormData) => void;
  onCancel: () => void;
  onManagePortions?: () => void;
  onManageToppings?: () => void;
}

const EditItemForm: React.FC<EditItemFormProps> = ({ initialData, vendorId, onSubmit, onCancel, onManagePortions, onManageToppings }) => {
  const navigate = useNavigate();
  // A stored allergenFree declaration renders as the "No known allergens"
  // sentinel inside the same multi-select (mapped back on submit).
  const [formData, setFormData] = useState({
    ...initialData,
    allergens: initialData.allergenFree ? [ALLERGEN_FREE_KEY] : (initialData.allergens || []),
    dietaryType: initialData.dietaryType || 'NONE',
  });
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { theme } = useTheme();
  const { deleteImage } = useImageUpload();
  const [currentImageKey, setCurrentImageKey] = useState<string | null>(initialData.imageKey || null);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(initialData.imageUrl || null);
  // Controlled so picking the exclusive "No known allergens" option can close
  // the menu immediately (multi-picking allergens keeps it open, as standard).
  const [allergenSelectOpen, setAllergenSelectOpen] = useState(false);

  const handleImageUploaded = useCallback(async (newKey: string) => {
    // If replacing an existing image, delete the old one from R2
    if (currentImageKey && currentImageKey !== newKey) {
      await deleteImage(currentImageKey);
    }
    setCurrentImageKey(newKey);
    setCurrentImageUrl(`${IMAGE_GATEWAY_URL}/${newKey}`);
    setFormData(prev => ({ ...prev, imageKey: newKey }));
  }, [currentImageKey, deleteImage]);

  const handleImageRemoved = useCallback(() => {
    setCurrentImageKey(null);
    setCurrentImageUrl(null);
    setFormData(prev => ({ ...prev, imageKey: undefined }));
  }, []);

  useEffect(() => {
    fetchKitchens();
  }, []);

  const fetchKitchens = async () => {
    try {
      setLoading(true);
      const data = await getKitchens();
      setKitchens(data);
    } catch (err) {
      setError('Failed to load kitchens');
      console.error('Error fetching kitchens:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      price: parseFloat(formData.price) || 0,
      quantity: formData.quantity ? parseInt(formData.quantity) : null,
      freeToppingsCount: formData.freeToppingsCount ? parseInt(formData.freeToppingsCount) : 0,
      extraToppingPrice: formData.extraToppingPrice ? parseFloat(formData.extraToppingPrice) : 0,
      kitchenId: formData.kitchenId ? parseInt(formData.kitchenId) : undefined,
      spiceLevel: formData.spiceLevel as SpiceLevel || 'NONE',
      allowsSpiceCustomization: formData.allowsSpiceCustomization || false,
      imageKey: currentImageKey || undefined,
      // Map the UI sentinel back to the allergenFree boolean + empty list
      allergens: (formData.allergens || []).includes(ALLERGEN_FREE_KEY) ? [] : (formData.allergens || []),
      allergenFree: (formData.allergens || []).includes(ALLERGEN_FREE_KEY),
      allergenNote: (formData.allergenNote || '').trim() || undefined,
      dietaryType: formData.dietaryType || 'NONE',
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        {vendorId && initialData.id && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: theme.text }}>
              Item Image
            </Typography>
            <MenuItemImageUpload
              vendorId={vendorId}
              type="items"
              entityId={initialData.id}
              imageUrl={currentImageUrl}
              imageKey={currentImageKey}
              onImageUploaded={handleImageUploaded}
              onImageRemoved={handleImageRemoved}
            />
          </Box>
        )}

        <FormInput
          label="Item Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          fullWidth
        />
        
        <FormInput
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          multiline
          rows={3}
          fullWidth
        />
        
        <FormInput
          label="Price"
          type="number"
          value={formData.price}
          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
          required
          fullWidth
          inputProps={{ step: 0.01, min: 0 }}
        />
        
        <FormInput
          label="Stock (Coming Soon)"
          type="number"
          value={formData.quantity}
          onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
          fullWidth
          disabled
          inputProps={{ min: 0 }}
          helperText="Stock management feature coming soon."
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
              cursor: 'not-allowed'
            }
          }}
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={formData.available}
              onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
            />
          }
          label="Available"
        />
        
        <Box display="flex" gap={2}>
          <FormInput
            label="Free Toppings Count"
            type="number"
            value={formData.freeToppingsCount || '0'}
            onChange={(e) => setFormData({ ...formData, freeToppingsCount: e.target.value })}
            fullWidth
            inputProps={{ min: 0 }}
            helperText="Number of toppings included at no extra cost"
          />
          
          <FormInput
            label="Extra Topping Price"
            type="number"
            value={formData.extraToppingPrice || '0'}
            onChange={(e) => setFormData({ ...formData, extraToppingPrice: e.target.value })}
            fullWidth
            inputProps={{ step: 0.01, min: 0 }}
            helperText="Price for each additional topping"
          />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        
        <FormControl fullWidth>
          <InputLabel id="kitchen-select-label">Kitchen</InputLabel>
          <Select
            labelId="kitchen-select-label"
            value={formData.kitchenId || ''}
            label="Kitchen"
            onChange={(e) => setFormData({ ...formData, kitchenId: e.target.value })}
            disabled={loading}
          >
            <MenuItem value="">
              <em>None (Default Kitchen)</em>
            </MenuItem>
            {kitchens.map((kitchen) => (
              <MenuItem key={kitchen.id} value={kitchen.id.toString()}>
                {kitchen.name} {kitchen.isDefault && '(Default)'} - {kitchen.kitchenType}
              </MenuItem>
            ))}
          </Select>
          {kitchens.length === 0 && !loading && (
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary">
                No kitchens available.{' '}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/vendor/kitchens')}
                  sx={{ textTransform: 'none' }}
                >
                  Create Kitchen
                </Button>
              </Typography>
            </Box>
          )}
        </FormControl>

        {/* Dietary marker — the classic green/red square-dot + vegan leaf */}
        <FormControl fullWidth>
          <InputLabel id="dietary-type-select-label">Dietary Type</InputLabel>
          <Select
            labelId="dietary-type-select-label"
            value={formData.dietaryType || 'NONE'}
            label="Dietary Type"
            onChange={(e) => setFormData({ ...formData, dietaryType: e.target.value })}
          >
            <MenuItem value="NONE">
              <em>Not declared</em>
            </MenuItem>
            {(['VEG', 'NON_VEG', 'VEGAN'] as const).map((t) => (
              <MenuItem key={t} value={t}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <DietaryDot type={t} size="md" />
                  {DIETARY_LABELS[t]}
                </span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="spice-level-select-label">Default Spice Level</InputLabel>
          <Select
            labelId="spice-level-select-label"
            value={formData.spiceLevel || 'NONE'}
            label="Default Spice Level"
            onChange={(e) => setFormData({ ...formData, spiceLevel: e.target.value })}
          >
            {Object.entries(SPICE_LEVELS).map(([key, config]) => (
              <MenuItem key={key} value={key}>
                {config.icon && <span style={{ marginRight: 8 }}>{config.icon}</span>}
                {config.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Allergen declaration (UK plan Phase 4) — same Select pattern as spice level */}
        <FormControl fullWidth>
          <InputLabel id="edit-allergen-select-label">Allergens (select all that apply)</InputLabel>
          <Select
            labelId="edit-allergen-select-label"
            multiple
            value={formData.allergens || []}
            label="Allergens (select all that apply)"
            open={allergenSelectOpen}
            onOpen={() => setAllergenSelectOpen(true)}
            onClose={() => setAllergenSelectOpen(false)}
            onChange={(e) => {
              const v = e.target.value;
              const next = typeof v === 'string' ? v.split(',') : v;
              // "No known allergens" is mutually exclusive with everything:
              // newly ticking it clears the allergens; ticking an allergen
              // while it is set clears it. It also CLOSES the menu — nothing
              // further can be selected, so keeping it open is dead air.
              // Multi-picking real allergens keeps the menu open (standard
              // multi-select behaviour; checkboxes signal the toggling).
              const hadNone = (formData.allergens || []).includes(ALLERGEN_FREE_KEY);
              const hasNone = next.includes(ALLERGEN_FREE_KEY);
              const resolved = hasNone && !hadNone
                ? [ALLERGEN_FREE_KEY]
                : next.filter((a) => a !== ALLERGEN_FREE_KEY);
              if (hasNone && !hadNone) setAllergenSelectOpen(false);
              setFormData({ ...formData, allergens: resolved });
            }}
            renderValue={(selected) => (selected as string[])
              .map((a) => (a === ALLERGEN_FREE_KEY ? `✓ ${ALLERGEN_FREE_LABEL}` : ALLERGEN_LABELS[a] || a))
              .join(', ')}
          >
            <MenuItem value={ALLERGEN_FREE_KEY} sx={{ color: 'success.main', fontWeight: 600 }}>
              ✓ {ALLERGEN_FREE_LABEL}
            </MenuItem>
            <Divider />
            {Object.entries(ALLERGEN_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                <Checkbox checked={(formData.allergens || []).includes(key)} size="small" sx={{ py: 0, pl: 0 }} />
                <ListItemText primary={label} />
              </MenuItem>
            ))}
            {/* Sticky footer: explicit dismissal for the stay-open multi-select
                (same "Done" pattern as the guest allergen filter panel) */}
            <Box
              sx={{
                position: 'sticky', bottom: 0, zIndex: 1,
                bgcolor: 'background.paper',
                borderTop: '1px solid', borderColor: 'divider',
                px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {(formData.allergens || []).includes(ALLERGEN_FREE_KEY)
                  ? ALLERGEN_FREE_LABEL
                  : `${(formData.allergens || []).length} selected`}
              </Typography>
              <PrimaryButton size="small" onClick={() => setAllergenSelectOpen(false)}>
                Done
              </PrimaryButton>
            </Box>
          </Select>
        </FormControl>

        <FormInput
          label="Allergen Note (optional)"
          value={formData.allergenNote || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, allergenNote: e.target.value })}
          placeholder="e.g. May contain traces of nuts"
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.allowsSpiceCustomization || false}
              onChange={(e) => setFormData({ ...formData, allowsSpiceCustomization: e.target.checked })}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'var(--status-bad)',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'var(--status-bad)',
                }
              }}
            />
          }
          label="Allow Customer Spice Customization"
        />

        <Box
          display="flex"
          gap={1}
          flexWrap="nowrap"
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: { xs: 'stretch', sm: 'space-between' }
          }}
        >
          {(onManagePortions || onManageToppings) && initialData.id && (
            <Box display="flex" gap={2} mb={1}>
              {onManagePortions && (
                <span
                  onClick={onManagePortions}
                  style={{
                    color: theme.accent || theme.primary,
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                  }}
                >
                  Portions
                </span>
              )}
              {onManageToppings && (
                <span
                  onClick={onManageToppings}
                  style={{
                    color: theme.gray?.[600] || '#555',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                  }}
                >
                  Toppings
                </span>
              )}
            </Box>
          )}
          <Box
            display="flex"
            gap={1}
            sx={{
              flexDirection: 'row',
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'stretch', sm: 'flex-end' }
            }}
          >
            <SecondaryButton
              onClick={onCancel}
              size="small"
              sx={{
                flex: { xs: 1, sm: 'none' },
                minWidth: { xs: 'auto', sm: 'fit-content' },
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                padding: { xs: '6px 12px', sm: '8px 16px' }
              }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              size="small"
              disabled={!formData.name || !formData.price}
              sx={{
                flex: { xs: 1, sm: 'none' },
                minWidth: { xs: 'auto', sm: 'fit-content' },
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                padding: { xs: '6px 12px', sm: '8px 16px' }
              }}
            >
              Update Item
            </PrimaryButton>
          </Box>
        </Box>
      </Stack>
    </form>
  );
};

export default EditItemForm;